import { CourseCardProps } from '../../base/CourseCard/CourseCard.types';
import { pipe } from '../../../../lib/utils/functional';
import {
  courseCardByCompliance,
  courseCardByStatus,
  courseCardByText,
  courseCardByType,
  mergeFilterData,
  TrainingsFilterConfig,
} from '../../../../lib/utils/search/searchHelper';
import { Facet, FacetType, FilterData } from '../../../../lib/utils/search/searchModel';
import { MY_COURSES } from '../../../../lib/utils/search/searchConstants';
import { Api } from '../../../../lib/types/apiSpec';

const byCourseName = (dir: number) => (course1: CourseCardProps, course2: CourseCardProps) =>
  dir * course1.name.localeCompare(course2.name);

const byCourseLastUpdated = (dir: number) => (course1: CourseCardProps, course2: CourseCardProps) => {
  if (!course1.lastUpdated) {
    return 1;
  }
  if (!course2.lastUpdated) {
    return -1;
  }

  return dir * (course1.lastUpdated.getTime() - course2.lastUpdated.getTime());
};

const byCourseDueDate = (dir: number) => (course1: CourseCardProps, course2: CourseCardProps) => {
  if (!course1.dueDate) {
    return 1;
  }
  if (!course2.dueDate) {
    return -1;
  }

  return dir * (new Date(course1.dueDate).getTime() - new Date(course2.dueDate).getTime());
};

export const filterTrainings = (filterFacets: FilterData<typeof FacetType.Checkbox>, config: TrainingsFilterConfig) =>
  pipe<CourseCardProps[]>(
    (arr: CourseCardProps[]) => arr.filter(courseCardByText(filterFacets.search)),
    (arr: CourseCardProps[]) => arr.filter(courseCardByType(filterFacets.facets ?? [])),
    (arr: CourseCardProps[]) => arr.filter(courseCardByStatus(filterFacets.facets ?? [])),
    (arr: CourseCardProps[]) => arr.filter(courseCardByCompliance(filterFacets.facets ?? [], config)),
  );

export const sortBy = (filterFacets: FilterData) => (trainings: CourseCardProps[]) => {
  const sorted = [...trainings];
  if (filterFacets) {
    const sort = filterFacets.sortBy ?? Api.SortBy.name;
    const direction = filterFacets.sortDirection === Api.SortDirection.desc ? -1 : 1;

    if (sort === Api.SortBy.name) {
      return sorted.sort(byCourseName(direction));
    }

    if (sort === Api.SortBy.lastUpdated) {
      return sorted.sort(byCourseLastUpdated(direction));
    }

    if (sort === Api.SortBy.dueDate) {
      return sorted.sort(byCourseDueDate(direction));
    }
  }

  return sorted;
};

const typeFacet = (
  trainings: CourseCardProps[],
  urlFilters: FilterData<typeof FacetType.Checkbox>,
  config: TrainingsFilterConfig,
): Facet<typeof FacetType.Checkbox> => {
  const urlFiltersWithoutType = {
    ...urlFilters,
    facets: urlFilters.facets.filter(facet => facet.name !== MY_COURSES.type.name),
  };
  const filteredTrainings = filterTrainings(urlFiltersWithoutType, config)(trainings);

  const buildValue = (value: string) => ({
    value,
    count:
      filterTrainings(
        {
          facets: [{ name: MY_COURSES.type.name, values: [{ value, selected: true }] }],
        },
        config,
      )(filteredTrainings).length ?? 0,
  });

  return {
    name: MY_COURSES.type.name,
    values: [buildValue(MY_COURSES.type.optionCourse), buildValue(MY_COURSES.type.optionLearningPath)],
  };
};

const statusFacet = (
  trainings: CourseCardProps[],
  urlFilters: FilterData<typeof FacetType.Checkbox>,
  config: TrainingsFilterConfig,
): Facet<typeof FacetType.Checkbox> => {
  const urlFiltersWithoutStatus = {
    ...urlFilters,
    facets: urlFilters.facets.filter(facet => facet.name !== MY_COURSES.status.name),
  };
  const filteredTrainings = filterTrainings(urlFiltersWithoutStatus, config)(trainings);

  const buildValue = (value: string) => ({
    value,
    count:
      filterTrainings(
        {
          facets: [{ name: MY_COURSES.status.name, values: [{ value, selected: true }] }],
        },
        config,
      )(filteredTrainings).length ?? 0,
  });

  return {
    name: MY_COURSES.status.name,
    values: [
      buildValue(MY_COURSES.status.optionComplete),
      buildValue(MY_COURSES.status.optionStarted),
      buildValue(MY_COURSES.status.optionNotStarted),
    ],
  };
};

const complianceFacet = (
  trainings: CourseCardProps[],
  urlFilters: FilterData<typeof FacetType.Checkbox>,
  config: TrainingsFilterConfig,
): Facet<typeof FacetType.Checkbox> => {
  const urlFiltersWithoutCompliance = {
    ...urlFilters,
    facets: urlFilters.facets.filter(facet => facet.name !== MY_COURSES.compliance.name),
  };
  const filteredTrainings = filterTrainings(urlFiltersWithoutCompliance, config)(trainings);

  const buildValue = (value: string) => ({
    value,
    count:
      filterTrainings(
        {
          facets: [{ name: MY_COURSES.compliance.name, values: [{ value, selected: true }] }],
        },
        config,
      )(filteredTrainings).length ?? 0,
  });

  return {
    name: MY_COURSES.compliance.name,
    values: [
      buildValue(MY_COURSES.compliance.optionCompliant),
      buildValue(MY_COURSES.compliance.optionNotCompliant),
      buildValue(MY_COURSES.compliance.optionRetakePeriod),
    ],
  };
};

export const withMyCoursesFacets =
  (trainings: CourseCardProps[], config: TrainingsFilterConfig) =>
  (filters: FilterData<typeof FacetType.Checkbox>): FilterData<typeof FacetType.Checkbox> => {
    return mergeFilterData({
      facets: [
        typeFacet(trainings, filters, config),
        statusFacet(trainings, filters, config),
        complianceFacet(trainings, filters, config),
      ],
    })(filters);
  };
